/**
 * Contour tools over the map: PLAIN ICON BUTTONS, no frame and no heading.
 *
 * They used to be a bordered panel titled «Контур поля» sitting in the same column as the
 * field roster. A titled box for four verbs is chrome explaining itself; the map is the
 * hero and the tools are marks on it. Each button keeps an accessible name and a tooltip,
 * so the words are still there for a screen reader and for a hover — they are just not
 * printed over the imagery.
 *
 * SORTED BY MODE, NOT STACKED IN A COLUMN. Seven glyphs in a row wrapped onto two lines;
 * the answer is not to turn the row on its side (248 px down the left edge of the map, in
 * the very mode where the map is being drawn on) but to stop showing the tools of a mode
 * nobody is in. Idle there is ONE — «Рисовать» — plus «Метка» and the delete button when a
 * field is selected. «Править черновик», «Отменить», «Вернуть» and «Стереть» all act on the
 * draft, so they live in the draft sheet, which already exists and already appears exactly
 * when a draft does. No new container, and a row that can no longer wrap.
 *
 * THERE IS NO IMPORT BUTTON, AND THAT IS NOT A REMOVAL OF THE FEATURE. A button whose whole
 * job is to open the system file picker spends a permanent slot on the map to save one
 * gesture; dropping the file onto the map does the same thing and says where the contour
 * will land while you are still holding it. The parser, its dictionary and its tests are
 * unchanged (lib/geo.ts) — only the entrance moved (map/drop.ts). The result is printed
 * here, in the sheet that was already showing it.
 *
 * The only other container is the draft sheet, and it appears ONLY while a contour is being
 * drawn or edited: naming and saving a polygon needs a text field, and a text field needs
 * a readable background. Idle, there is nothing but the icons.
 */

import { useEffect, useId, useRef, useState } from "react";

import { T } from "../strings";
import type { GeoJsonPolygon } from "../api/types";
import type { GeometryIssue } from "../lib/validateGeometry";
import { hectares } from "../lib/format";
import {
  POLICY_MARKER_MAX_DISTANCE_M,
  POLICY_MARKERS_PER_FIELD,
  POLICY_SMALL_FIELD_AREA_HA,
  PIPELINE_MAX_FIELD_AREA_HA,
} from "../lib/constants";

export type DrawMode = "idle" | "draw" | "edit";

/**
 * Чего ждёт следующий клик по карте.
 *
 * ДВА РЕЖИМА ПОСТАНОВКИ, А НЕ ОДИН. `new` взводит тулбар: клик заводит НОВУЮ заметку с
 * попапом-черновиком. `place` взводит карточка в панели: у готовой заметки не было места, и
 * клик указывает ЕЁ место — ни черновика, ни попапа, ни вопроса. Лист тулбара обязан
 * различать их словами, потому что в режиме `place` человек уже не видит, какую из двадцати
 * карточек он открыл, — `what` и есть ответ на это (`markers.placing`).
 */
export type MarkerTarget = { kind: "new" } | { kind: "place"; noteId: number; what: string };

/**
 * ОДИН СЛОВАРЬ НА ВЕСЬ ЭКРАН. `calendar` и `eye` живут здесь, а рисуются в пилюле даты
 * (PreviewControl): орган, стоящий среди иконок, обязан быть набран тем же штрихом, иначе
 * он читается как мусор. Второй набор путей в соседнем файле — это и есть та вилка, из-за
 * которой одна и та же вещь начинает выглядеть по-разному в двух углах одной карты.
 *
 * ОДИН ОПТИЧЕСКИЙ БОКС. У каждого глифа основная масса занимает 16 из 24 единиц, крайние
 * точки лежат на 3.5 или 20.5, и ни один путь не выходит за 3..21 — иначе девять иконок в
 * одном ряду набраны разными кеглями. Проверяется тестом, а не глазами.
 */
export const glyph = {
  draw: "M4 19 L7 6 L17 5 L20 15 L11 20 Z",
  // Карандаш, а не четырёхугольник: рядом с `draw` в одном ряду прежняя фигура была от него
  // неотличима, а слово у кнопки — «Править».
  edit: "M4 20 L5.5 15.5 L16 4.5 L19.5 8 L9 18.5 Z M14 6.5 L17.5 10",
  clear: "M4 20 L20 20 M7 16 L14.5 5 L19.5 8.5 L12 17",
  // Флажок на древке: та же форма, что и метка на карте (components/FieldNotes.css), чтобы
  // кнопка и то, что она ставит, читались как одна вещь.
  flag: "M6 20.5 L6 3.5 M6 5 L17 5 L17 12 L6 12",
  remove: "M5 7 L19 7 M10 7 L10 4 L14 4 L14 7 M7 7 L8 20 L16 20 L17 7",
  // Плюс — плитка «добавить» в начале рельсы модалки. Размах 15 из 24, как у соседей по
  // словарю: крест мельче читался бы в ряду миниатюр не знаком, а соринкой.
  plus: "M12 4.5 L12 19.5 M4.5 12 L19.5 12",
  undo: "M4 11 L9 6 M4 11 L9 16 M4 11 L14 11 A5 5 0 0 1 14 21 L11 21",
  redo: "M20 11 L15 6 M20 11 L15 16 M20 11 L10 11 A5 5 0 0 0 10 21 L13 21",
  // Сетка с двумя ушками. Ушки кончаются НА верхней грани: уходя за неё, они торчали внутрь
  // шапки календаря и читались как две лишние засечки.
  calendar: "M4 6 L20 6 L20 20 L4 20 Z M4 10 L20 10 M9 3.5 L9 6 M15 3.5 L15 6",
  // Глаз: линза и зрачок. Зрачок вынесен отдельным путём, потому что заливка его и есть
  // не-цветовой различитель включённого слоя (`pupil` ниже). Размах линзы 5..19 — прежние
  // 6.5..17.5 делали глаз мельче всех соседей в ряду.
  eye: "M3.5 12 A10 10 0 0 1 20.5 12 A10 10 0 0 1 3.5 12 Z M9 12 A3 3 0 1 1 15 12 A3 3 0 1 1 9 12 Z",
} as const;

/** Зрачок глаза, залитый: нажатое состояние читается формой, а не только цветом. */
export const glyphSolid = {
  pupil: "M9 12 A3 3 0 1 1 15 12 A3 3 0 1 1 9 12 Z",
} as const;

/**
 * 18 px на сетке 24: штрих 2 даёт ровно 1.5 устройственных пикселя — тот же вес, что у
 * `--hair`-линеек вокруг. Было 1.6 на 17/24, то есть 1.13 px: глиф садился на полупиксель и
 * мылился на не-Retina. `butt` вместо `square`: квадратный конец добавлял полуштриховый вынос
 * на каждом открытом конце, и именно из-за него древко флажка и ушки календаря вылезали за
 * оптический бокс. Те же два числа записаны токенами `--icon-size` / `--icon-stroke`: атрибут
 * SVG значения `var()` не принимает, поэтому здесь они литералами, а токены — договор, по
 * которому их сверяет тест.
 *
 * `size` и `stroke` — НЕОБЯЗАТЕЛЬНЫЕ, и дефолты 18/2 не меняются ни на один вызов: два числа
 * выше выведены для кнопок панели, и трогать их ради одного места значило бы перекроить весь
 * ряд. Единственное отступление было поимённым — чип привязи на плитке фотографии, — и
 * умерло вместе со своей поверхностью (notes-v2): плитки нет, привязаны все, и значка «у
 * этого кадра есть место» больше не существует. Параметры оставлены, но НИ ОДИН вызов их
 * сейчас не передаёт: следующее отступление обязано быть таким же поимённым, а штрих обязан
 * уходить вместе с размером — знак, ужатый до двух третей, становится вдвое жирнее соседей.
 */
export function Glyph({
  d,
  solid,
  size = 18,
  stroke = 2,
}: {
  d: string;
  solid?: string;
  size?: number;
  stroke?: number;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="butt"
      strokeLinejoin="miter"
      aria-hidden="true"
      focusable="false"
    >
      <path d={d} />
      {solid && <path d={solid} fill="currentColor" stroke="none" />}
    </svg>
  );
}

interface Props {
  mode: DrawMode;
  onModeChange: (mode: DrawMode) => void;
  draft: { geometry: GeoJsonPolygon; areaHa: number; name: string } | null;
  onClearDraft: () => void;
  onNameChange: (name: string) => void;
  onSave: () => void;
  saving: boolean;
  selectedFieldName: string | null;
  onDeleteField: () => void;
  /**
   * Placing a marker is a MODE, not a click: the point is chosen on the map, and the map is
   * also how you select a field, drag a vertex and pan. Arming it explicitly is what keeps
   * those four apart. Available only with a field selected — a marker belongs to a field.
   *
   * `null` — режима нет. Взводит его либо кнопка тулбара (`new`), либо карточка в панели
   * (`place`); тулбар рисует лист для обоих, потому что лист принадлежит КАРТЕ, а не тому,
   * кто режим включил.
   */
  markerTarget: MarkerTarget | null;
  /**
   * Есть ли на этом экране поверхность заметок вообще.
   *
   * `false` в запечатанном режиме, и кнопка тогда НЕ ГАСНЕТ, А ИСЧЕЗАЕТ: мёртвый орган без
   * объяснения хуже отсутствующего — он обещает действие и молчит о том, почему его нет. В
   * запечатанном режиме заметок нет целиком (ни полосы, ни панели, ни флажков), и путь
   * оператора здесь один — форма наблюдения.
   */
  notesEnabled?: boolean;
  onMarkerMode: (on: boolean) => void;
  /** Why the server refused the last point. Printed under the toolbar; the mode stays on. */
  markerError: string | null;
  /** The field is at the server's ceiling: the click answers with the ceiling, not a 409. */
  markersAtLimit?: boolean;
  /** The ceiling itself, as the server stated it — the refusal has to name the number. */
  markerLimit?: number;
  /** Result of a file dropped on the map (map/drop.ts): what was created and what was not. */
  importNote?: string | null;
  importError?: string | null;
  /** Closes a failed import's report; a refusal list stands until this is pressed. */
  onImportDismiss?: () => void;
  maxAreaHa?: number;
  /** Server policy threshold (schemas.SMALL_FIELD_AREA_HA); the constant is the fallback. */
  smallAreaHa?: number;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  /** What is wrong with the contour right now; a self-intersection blocks the save. */
  issues: GeometryIssue[];
}

export function DrawTools({
  mode,
  onModeChange,
  draft,
  onClearDraft,
  onNameChange,
  onSave,
  saving,
  selectedFieldName,
  onDeleteField,
  markerTarget,
  notesEnabled = true,
  onMarkerMode,
  markerError,
  markersAtLimit = false,
  markerLimit = POLICY_MARKERS_PER_FIELD,
  importNote = null,
  importError = null,
  onImportDismiss,
  maxAreaHa = PIPELINE_MAX_FIELD_AREA_HA,
  smallAreaHa = POLICY_SMALL_FIELD_AREA_HA,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  issues,
}: Props) {
  const nameInput = useRef<HTMLInputElement>(null);
  const nameFieldId = useId();
  // The ring is closed by the time a draft exists, so taking the caret is not stealing it
  // from the map — it is handing it to the only thing left to do before saving. Selected,
  // not just focused: the default is a suggestion, and typing must replace it whole.
  const hasDraft = draft !== null;
  useEffect(() => {
    if (!hasDraft) return;
    nameInput.current?.focus();
    nameInput.current?.select();
  }, [hasDraft]);
  // Deleting a field takes its whole run history with it, and the button used to sit 34 px
  // from «Стереть» differing only in colour. Two steps in the row itself, disarming on its
  // own after five seconds — no window.confirm (a system dialog is not a field instrument)
  // and no modal.
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    if (!armed) return;
    const timer = setTimeout(() => setArmed(false), 5000);
    return () => clearTimeout(timer);
  }, [armed]);

  // A field goes away, or another one is selected: an armed question about the old name is
  // the one thing that must not survive that.
  useEffect(() => setArmed(false), [selectedFieldName]);

  // The ceiling sentence is printed AT THE MOMENT OF INTENT — on the click that wanted a
  // marker — not as permanent furniture under the toolbar (markers-limit-on-click). It dies
  // with the field it is about, and the moment the ceiling itself is gone.
  const [limitAsked, setLimitAsked] = useState(false);
  useEffect(() => setLimitAsked(false), [selectedFieldName]);
  useEffect(() => {
    if (!markersAtLimit) setLimitAsked(false);
  }, [markersAtLimit]);

  // A mode about a field cannot outlive the selection: with the field gone the next click
  // would place a marker on nothing. The ceiling ends it for the same reason — the mode
  // would be armed for a click the server is bound to refuse.
  //
  // ПОТОЛОК ГАСИТ ТОЛЬКО РЕЖИМ `new`: он про создание, а `place` указывает место заметке,
  // которая УЖЕ существует и в квоту давно вошла. Погасить его на полном поле значило бы
  // запретить чинить ровно то, что и привело поле к потолку.
  const marking = markerTarget !== null;
  const creating = markerTarget?.kind === "new";
  useEffect(() => {
    if (marking && selectedFieldName === null) onMarkerMode(false);
    else if (creating && markersAtLimit) onMarkerMode(false);
  }, [selectedFieldName, marking, creating, markersAtLimit, onMarkerMode]);

  // Escape leaves the mode. On `window` and only WHILE the mode is on, so this is not a
  // fourth permanent global listener arguing with the three that already exist. A popup of
  // its own is open over the map at that moment and Leaflet closes it with the same key —
  // whoever is typing in it gets the popup, not the mode.
  useEffect(() => {
    if (!marking) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      if ((event.target as HTMLElement | null)?.closest?.(".leaflet-popup")) return;
      onMarkerMode(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [marking, onMarkerMode]);

  const tooBig = draft !== null && draft.areaHa > maxAreaHa;
  const tooSmall = draft !== null && draft.areaHa < smallAreaHa;
  const drafting = mode !== "idle" || draft !== null;
  // A ring in progress is a ring with too few points and with the cursor sitting on top of
  // the last one — both are TRUE of every contour a second after it is started, and saying
  // so is noise, not help. Mid-draw only the two that stay wrong are printed; all of them
  // still hold the save button shut.
  const drawingNoise = new Set(["too_few_points", "duplicate_points"]);
  const shown = issues.filter((issue) => !(mode === "draw" && drawingNoise.has(issue.kind)));
  const broken = issues.length > 0;

  return (
    <div className="k-tools">
      <div className="k-tools__bar" role="group" aria-label={T.tools.groupLabel}>
        <button
          type="button"
          className="k-iconbtn"
          aria-pressed={mode === "draw"}
          aria-label={mode === "draw" ? T.tools.stop : T.tools.draw}
          title={mode === "draw" ? T.tools.stop : T.tools.draw}
          onClick={() => onModeChange(mode === "draw" ? "idle" : "draw")}
        >
          <Glyph d={glyph.draw} />
        </button>
        {selectedFieldName && notesEnabled && (
          <button
            type="button"
            className="k-iconbtn"
            aria-pressed={creating}
            aria-label={T.markers.add}
            title={T.markers.add}
            // Clickable AT the ceiling on purpose: a greyed control with an unreliable
            // `title` explains nothing, so the click itself earns the sentence instead.
            onClick={() => {
              if (markersAtLimit) {
                setLimitAsked(true);
                return;
              }
              onMarkerMode(!creating);
            }}
          >
            <Glyph d={glyph.flag} />
          </button>
        )}
        {selectedFieldName && (
          <button
            type="button"
            className="k-iconbtn k-iconbtn--danger"
            aria-label={T.tools.delete}
            title={T.tools.delete}
            aria-expanded={armed}
            onClick={() => setArmed(true)}
          >
            <Glyph d={glyph.remove} />
          </button>
        )}
      </div>

      {/* `alert`, not `alertdialog`: the question is announced when it appears, and we owe
          none of the focus-trapping an actual dialog promises. */}
      {armed && selectedFieldName && (
        <div className="k-tools__sheet k-tools__sheet--ask" role="alert">
          <p className="k-hint">{T.tools.deleteAsk(selectedFieldName)}</p>
          <div className="k-tools__askrow">
            <button
              type="button"
              className="k-btn k-btn--danger"
              onClick={() => {
                setArmed(false);
                onDeleteField();
              }}
            >
              {T.tools.deleteYes}
            </button>
            <button type="button" className="k-btn k-btn--ghost" onClick={() => setArmed(false)}>
              {T.tools.deleteNo}
            </button>
          </div>
        </div>
      )}

      {/* The limit is named BEFORE the click, not learned from a refusal after it: a point
          refused for being 40 m too far costs a click and explains nothing at the moment it
          is spent. A refusal that did happen stands under the same line, and the mode stays
          armed — you are meant to try again a little closer. */}
      {markerTarget !== null && (
        <div className="k-tools__sheet" data-testid="marker-sheet">
          {/* ДВА РЕЖИМА — ДВЕ СТРОКИ. «Клик по карте ставит заметку» в режиме, начатом из
              карточки, было бы обещанием ВТОРОЙ заметки; там указывают место первой, и строка
              называет, ЧЬЁ именно, — на карте карточки не видно. */}
          <p className="k-hint">
            {markerTarget.kind === "place"
              ? T.markers.placing(markerTarget.what, POLICY_MARKER_MAX_DISTANCE_M)
              : T.markers.hint(POLICY_MARKER_MAX_DISTANCE_M)}
          </p>
          {markerError && (
            <div className="k-note k-note--error" role="alert">
              {markerError}
            </div>
          )}
          {/* Esc мышью не нажимается, а до тулбара курсор и так дойдёт: лист объявил режим —
              он же обязан дать из него выход. */}
          <button
            type="button"
            className="k-btn k-btn--ghost k-btn--compact"
            data-testid="marker-cancel"
            onClick={() => onMarkerMode(false)}
          >
            {T.fieldCard.cancel}
          </button>
        </div>
      )}

      {/* The ceiling is printed in answer to a click, not standing on every screen of a full
          field: the sentence appears the moment somebody wanted a marker and names what to
          do (its own text says «Удалите ненужную»). */}
      {selectedFieldName && markersAtLimit && limitAsked && (
        <div className="k-tools__sheet" role="alert">
          <p className="k-hint">{T.markers.limit(markerLimit)}</p>
        </div>
      )}

      {drafting && (
        <div className="k-tools__sheet">
          {/* The four verbs of the draft, with the draft. Undo and redo stand next to what
              they undo; the keyboard does the same from anywhere on the screen. */}
          <div className="k-tools__bar" role="group" aria-label={T.tools.groupLabel}>
            <button
              type="button"
              className="k-iconbtn"
              aria-pressed={mode === "edit"}
              aria-label={T.tools.edit}
              title={T.tools.edit}
              onClick={() => onModeChange(mode === "edit" ? "idle" : "edit")}
            >
              <Glyph d={glyph.edit} />
            </button>
            <button
              type="button"
              className="k-iconbtn"
              disabled={!canUndo}
              aria-label={T.draw.undo}
              title={T.draw.undo}
              onClick={onUndo}
            >
              <Glyph d={glyph.undo} />
            </button>
            <button
              type="button"
              className="k-iconbtn"
              disabled={!canRedo}
              aria-label={T.draw.redo}
              title={T.draw.redo}
              onClick={onRedo}
            >
              <Glyph d={glyph.redo} />
            </button>
            <button
              type="button"
              className="k-iconbtn"
              disabled={!draft}
              aria-label={T.tools.clear}
              title={T.tools.clear}
              onClick={() => {
                onClearDraft();
                onModeChange("idle");
              }}
            >
              <Glyph d={glyph.clear} />
            </button>
          </div>

          {mode === "draw" && <p className="k-hint">{T.tools.drawHint}</p>}
          {mode === "edit" && <p className="k-hint">{T.tools.editHint}</p>}

          {/* THE NAME COMES FIRST, AND IT IS FOCUSED. It used to sit under the area and the
              warnings, prefilled with nothing, and «Сохранить» worked without it — so nobody
              filled it in and the owner got four fields called «Поле 03.08.2026». The box is
              prefilled with the contour's own coordinates (lib/fieldName.ts) and the caret is
              in it the moment the ring closes: typing over it is the expected act. */}
          {draft && (
            <div className="k-field">
              <label className="k-label" htmlFor={nameFieldId}>
                {T.tools.nameLabel}
              </label>
              <input
                ref={nameInput}
                id={nameFieldId}
                className="k-input"
                value={draft.name}
                placeholder={T.tools.namePlaceholder}
                onChange={(event) => onNameChange(event.target.value)}
              />
              <p className="k-hint">{T.tools.nameHint}</p>
            </div>
          )}

          <div className="k-area">
            <span className="k-label">{T.tools.area}</span>
            <span className="k-area__value k-mono">
              {draft ? `${hectares(draft.areaHa)} ${T.tools.hectares}` : T.tools.areaPending}
            </span>
          </div>

          {tooBig && <div className="k-note k-note--error">{T.tools.tooBig(maxAreaHa)}</div>}
          {tooSmall && (
            <div className="k-note k-note--warn">{T.tools.smallField(smallAreaHa, null)}</div>
          )}

          {shown.map((issue) => (
            <div
              key={issue.kind}
              className={
                issue.kind === "self_intersection" || issue.kind === "out_of_range"
                  ? "k-note k-note--error"
                  : "k-note k-note--warn"
              }
            >
              {issue.message}
            </div>
          ))}

          {draft && (
            <button
              type="button"
              className="k-btn k-btn--primary k-btn--block"
              disabled={saving || tooBig || broken}
              onClick={onSave}
            >
              {T.tools.save}
            </button>
          )}
        </div>
      )}

      {(importNote || importError) && (
        <div className="k-tools__sheet">
          {importNote && <p className="k-hint">{importNote}</p>}
          {/* A refusal list stays until it is CLOSED (drop-notice-sticky): five reasons do
              not read themselves in four seconds. The success line still fades on its own. */}
          {importError && (
            <div className="k-note k-note--error">
              {importError}
              {onImportDismiss && (
                <button
                  type="button"
                  className="k-btn k-btn--ghost k-btn--compact"
                  onClick={onImportDismiss}
                >
                  {T.common.close}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
